import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { AuthResponse } from '../Interfaces/response.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthTokenService {
  private readonly _localStorage = inject(LocalStorageService);

  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'auth_refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'auth_token_expiry';
  private readonly RETURN_URL_KEY = 'auth_return_url';

  setToken(auth_data: AuthResponse): void {
    const expiry_time = new Date().getTime() + auth_data.expires_in * 1000;

    this._localStorage.setItem(this.TOKEN_KEY, auth_data.access_token);

    if (auth_data.refresh_token) {
      this._localStorage.setItem(this.REFRESH_TOKEN_KEY, auth_data.refresh_token);
    } else {
      this._localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }

    this._localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiry_time.toString());
  }

  getToken(): string | null {
    return this._localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this._localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  isTokenExpired(): boolean {
    const expiry = this._localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (!expiry) {
      return true;
    }

    const parsed = parseInt(expiry, 10);

    if (Number.isNaN(parsed)) {
      return true;
    }

    return new Date().getTime() > parsed;
  }

  hasValidToken(): boolean {
    return !!this.getToken() && !this.isTokenExpired();
  }

  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  clearToken(): void {
    this._localStorage.removeItem(this.TOKEN_KEY);
    this._localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this._localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
  }

  setReturnUrl(returnUrl: string): void {
    this._localStorage.setItem(this.RETURN_URL_KEY, returnUrl);
  }

  getAndClearReturnUrl(): string {
    const returnUrl = this._localStorage.getItem(this.RETURN_URL_KEY) || '/setup';
    this._localStorage.removeItem(this.RETURN_URL_KEY);

    return returnUrl.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : '/setup';
  }
}
